import { ArrowUpRightSm } from "@alchemy/ui/icons/16";
import classNames from "classnames";
import { useCallback, useRef, useState } from "react";

import { ViewAsPreservingLink } from "../../common/ViewAsPreservingLink";
import { NavLink, isExternalLink, isInternalLink } from "./use-nav-items";

export interface NavButtonProps {
  item: NavLink;
  onClick?: (item: NavLink) => void;
  active?: boolean;
}

const buttonClassName = classNames(
  "group my-0.5 flex h-10 w-full items-center gap-2 rounded-lg px-2 text-left text-paragraph-200-regular text-white outline-none transition",
  "hover:cursor-pointer hover:bg-alpha-white-50",
  "aria-current-page:bg-secondary-200/20 aria-current-page:text-paragraph-200-medium",
  "aria-current-page:data-hovered:bg-secondary-200",
  "focus-visible:shadow-focus-dark",
);

const leadingIconClassName = classNames(
  "flex h-5 w-5 flex-shrink-0 items-center justify-center text-grayscale-400 transition",
  "group-hover:text-inherit",
  "group-aria-current-page:text-secondary-200 group-aria-current-page:transition-none",
);

const labelClassName = classNames(
  "flex-1 min-w-0 overflow-hidden text-ellipsis whitespace-nowrap",
);

const trailingIconClassName = classNames(
  "-mr-1 text-grayscale-500 group-aria-current-page:text-inherit",
);

const newBadgeClassName = classNames(
  "rounded bg-primary-400 px-3 py-1.5 text-label-100 text-white",
);

const NavButton = ({ item, onClick, active }: NavButtonProps) => {
  const [hideTrailingIcon, setHideTrailingIcon] = useState(false);
  const labelRef = useRef<HTMLSpanElement>(null);

  const external = isExternalLink(item);
  const internal = isInternalLink(item);

  const handleClick = useCallback(() => {
    onClick?.(item);
  }, [item, onClick]);

  const commonProps = {
    className: buttonClassName,
    onClick: handleClick,
    "aria-current": active ? ("page" as const) : undefined,
  };

  const commonContent = (
    <>
      <span className={leadingIconClassName}>
        <item.icon />
      </span>

      <span className={labelClassName} ref={labelRef}>
        {item.label}
      </span>

      {item.highlight && !active && (
        <span className={newBadgeClassName}>New!</span>
      )}
    </>
  );

  const handleHideTrailingIcon = useCallback(() => {
    const labelElement = labelRef.current;

    if (!labelElement) return;

    const labelTruncated = labelElement.offsetWidth < labelElement.scrollWidth;

    if (labelTruncated) {
      setHideTrailingIcon(true);
    }
  }, []);

  const handleShowTrailingIcon = useCallback(() => {
    if (hideTrailingIcon) {
      setHideTrailingIcon(false);
    }
  }, [hideTrailingIcon]);

  return (
    <li>
      {external ? (
        <a
          href={item.href}
          target="_blank"
          rel="noreferrer noopener"
          onMouseEnter={handleHideTrailingIcon}
          onMouseLeave={handleShowTrailingIcon}
          onFocus={handleHideTrailingIcon}
          onBlur={handleShowTrailingIcon}
          {...commonProps}
        >
          {commonContent}

          {hideTrailingIcon ? null : (
            <span className={trailingIconClassName}>
              <ArrowUpRightSm />
            </span>
          )}
        </a>
      ) : internal ? (
        <ViewAsPreservingLink to={item.to} {...commonProps}>
          {commonContent}
        </ViewAsPreservingLink>
      ) : (
        <button {...commonProps}>{commonContent}</button>
      )}
    </li>
  );
};

export default NavButton;
